import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Button,
    CircularProgress,
    Collapse,
    IconButton,
    Paper,
    Dialog,
    Tooltip
} from '@mui/material';
import { Fullscreen, Close, FiberManualRecord, ExpandMore, ChevronRight, Download } from '@mui/icons-material';
import ServiciosCalificacion from './ServiciosCalificacion';

const detectarTipoArchivo = (url = '') => {
    if (/\.pdf($|\?)/i.test(url)) return 'pdf';
    if (/\.(mp4|webm|mov|avi|mkv|wmv|flv|m4v|3gp|mpeg|mpg|ogv)($|\?)/i.test(url)) return 'video';
    if (/\.(mp3|wav|ogg|m4a|aac|flac|opus|wma|aiff|amr)($|\?)/i.test(url)) return 'audio';
    if (/\.(jpg|jpeg|png|gif|webp|bmp|svg|tiff|tif)($|\?)/i.test(url)) return 'imagen';
    return 'descargable';
};

const extraerNombreArchivo = (ruta = '') => {
    try {
        const sinQuery = ruta.split('?')[0];
        return decodeURIComponent(sinQuery.split('/').pop() || '');
    } catch (e) {
        return ruta;
    }
};

const extraerExtension = (ruta = '') => {
    const nombre = extraerNombreArchivo(ruta);
    const partes = nombre.split('.');
    return partes.length > 1 ? partes.pop().toLowerCase() : '';
};

const normalizarParaNombre = (texto = '') =>
    texto
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .replace(/[^a-zA-Z0-9\s_-]/g, '')
        .replace(/\s+/g, '_')
        .toLowerCase();

const formatearNombreEvidencia = (nombreColaborador, tituloMaterial, rutaArchivo) => {
    const extension = extraerExtension(rutaArchivo);
    const base = normalizarParaNombre(`${nombreColaborador}_${tituloMaterial}`);
    return extension ? `${base}.${extension}` : base;
};

const estiloBotonNotable = {
    backgroundColor: '#e0e0e0',
    color: '#000000',
    borderRadius: '999px',
    boxShadow: '0px 2px 6px rgba(0,0,0,0.25)',
    textTransform: 'none',
    px: 2,
    '&:hover': {
        backgroundColor: '#d5d5d5',
        boxShadow: '0px 3px 8px rgba(0,0,0,0.3)'
    }
};

const ModalEvidenciaColaborador = ({ colaborador, idCurso, nombreCurso, nombreGrupo }) => {
    const serviciosCalificacion = ServiciosCalificacion();

    const [sesiones, setSesiones] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [seleccion, setSeleccion] = useState(null);
    const [pantallaCompleta, setPantallaCompleta] = useState(false);
    const [descargando, setDescargando] = useState(null); // idDetalleActividad en descarga

    const [sesionesAbiertas, setSesionesAbiertas] = useState([]);
    const [actividadesAbiertas, setActividadesAbiertas] = useState([]);

    useEffect(() => {
        serviciosCalificacion.getEvidenciaColaborador(
            (data) => {
                const listaSesiones = data.sesiones || [];
                setSesiones(listaSesiones);
                setCargando(false);

                setSesionesAbiertas(listaSesiones.map((s) => s.idSesion));
                const todasLasActividades = listaSesiones.flatMap((s) => s.actividades.map((a) => a.idActividad));
                setActividadesAbiertas(todasLasActividades);
            },
            { idCurso, idColaborador: colaborador.idColaborador }
        );
    }, [idCurso]);

    const toggleSesion = (idSesion) => {
        setSesionesAbiertas((prev) => (prev.includes(idSesion) ? prev.filter((id) => id !== idSesion) : [...prev, idSesion]));
    };

    const toggleActividad = (idActividad) => {
        setActividadesAbiertas((prev) => (prev.includes(idActividad) ? prev.filter((id) => id !== idActividad) : [...prev, idActividad]));
    };

    const verMaterial = (material, tituloActividad) => {
        setSeleccion({ titulo: material.titulo, tituloActividad, archivoEvidencia: material.archivoEvidencia, cargando: true });
        serviciosCalificacion.getMaterialEvidencia(
            (res) => {
                if (res.status !== 1) {
                    setSeleccion({
                        titulo: material.titulo,
                        tituloActividad,
                        archivoEvidencia: material.archivoEvidencia,
                        sinEvidencia: true,
                        mensaje: res.mensaje,
                        cargando: false
                    });
                    return;
                }
                setSeleccion({
                    titulo: material.titulo,
                    tituloActividad,
                    archivoEvidencia: material.archivoEvidencia,
                    material: res.material,
                    cargando: false
                });
            },
            { idDetalleActividad: material.idDetalleActividad, idColaborador: colaborador.idColaborador }
        );
    };

    const abrirPantallaCompleta = () => setPantallaCompleta(true);
    const cerrarPantallaCompleta = () => setPantallaCompleta(false);

    // Descarga via signed URL con responseDisposition=attachment (Google fuerza la descarga, sin fetch/blob)
    const descargarEvidencia = (material) => {
        if (descargando) return;
        setDescargando(material.idDetalleActividad);

        const nombreDescarga = formatearNombreEvidencia(colaborador.nombre_persona, material.titulo, material.archivoEvidencia || '');

        serviciosCalificacion.getUrlDescargaEvidencia(
            (res) => {
                setDescargando(null);
                if (res.status === 1 && res.url) {
                    window.location.href = res.url;
                }
            },
            {
                idDetalleActividad: material.idDetalleActividad,
                idColaborador: colaborador.idColaborador,
                nombreDescarga
            }
        );
    };

    const renderMaterial = (material, alturaMax = 360) => {
        const url = material.linkVideo;
        if (!url) {
            return (
                <Typography variant="body2" color="textSecondary">
                    No fue posible obtener el archivo de evidencia.
                </Typography>
            );
        }
        const tipo = detectarTipoArchivo(url);

        if (tipo === 'pdf') {
            const srcPdf = material.pdf ? `data:application/pdf;base64,${material.pdf}` : url;
            return <iframe src={srcPdf} title="Evidencia PDF" width="100%" height={alturaMax} style={{ border: 'none' }} />;
        }
        if (tipo === 'video') {
            return (
                <video controls width="100%" style={{ maxHeight: alturaMax }}>
                    <source src={url} />
                </video>
            );
        }
        if (tipo === 'audio') {
            return (
                <audio controls style={{ width: '100%' }}>
                    <source src={url} />
                </audio>
            );
        }
        if (tipo === 'imagen') {
            return <Box component="img" src={url} alt="Evidencia" sx={{ width: '100%', maxHeight: alturaMax, objectFit: 'contain' }} />;
        }
        return (
            <Button variant="text" href={url} target="_blank" rel="noreferrer" startIcon={<Download />} sx={estiloBotonNotable}>
                Descargar evidencia
            </Button>
        );
    };

    return (
        <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Evidencia del curso y grupo seleccionado:
                </Typography>
                <List dense disablePadding>
                    <ListItem disableGutters sx={{ py: 0.25 }}>
                        <FiberManualRecord sx={{ fontSize: 6, mr: 1 }} />
                        <ListItemText
                            primary={
                                <Typography variant="body2">
                                    Grupo: <strong>{nombreGrupo || '-'}</strong>
                                </Typography>
                            }
                        />
                    </ListItem>
                    <ListItem disableGutters sx={{ py: 0.25 }}>
                        <FiberManualRecord sx={{ fontSize: 6, mr: 1 }} />
                        <ListItemText
                            primary={
                                <Typography variant="body2">
                                    Curso: <strong>{nombreCurso || '-'}</strong>
                                </Typography>
                            }
                        />
                    </ListItem>
                </List>
            </Box>

            <Box sx={{ display: 'flex', gap: 2.5, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch' }}>
                <Paper variant="outlined" sx={{ flex: '0 0 40%', p: 1.5, borderRadius: 2, maxHeight: 460, overflowY: 'auto' }}>
                    {cargando ? (
                        <CircularProgress size={24} />
                    ) : sesiones.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">
                            No hay materiales que soliciten evidencia registrados para este curso.
                        </Typography>
                    ) : (
                        <List dense disablePadding>
                            {sesiones.map((sesion) => (
                                <div key={sesion.idSesion} style={{ marginBottom: 4 }}>
                                    <ListItemButton onClick={() => toggleSesion(sesion.idSesion)} sx={{ borderRadius: 1 }}>
                                        <IconButton size="small" edge="start" sx={{ mr: 0.5 }}>
                                            {sesionesAbiertas.includes(sesion.idSesion) ? <ExpandMore /> : <ChevronRight />}
                                        </IconButton>
                                        <ListItemText primary={sesion.titulo} primaryTypographyProps={{ fontWeight: 500 }} />
                                    </ListItemButton>

                                    <Collapse in={sesionesAbiertas.includes(sesion.idSesion)} timeout="auto" unmountOnExit>
                                        <List dense disablePadding sx={{ pl: 2 }}>
                                            {sesion.actividades.map((actividad) => (
                                                <div key={actividad.idActividad}>
                                                    <ListItemButton
                                                        onClick={() => toggleActividad(actividad.idActividad)}
                                                        sx={{ borderRadius: 1 }}
                                                    >
                                                        <IconButton size="small" edge="start" sx={{ mr: 0.5 }}>
                                                            {actividadesAbiertas.includes(actividad.idActividad) ? (
                                                                <ExpandMore />
                                                            ) : (
                                                                <ChevronRight />
                                                            )}
                                                        </IconButton>
                                                        <ListItemText primary={actividad.titulo} />
                                                    </ListItemButton>

                                                    <Collapse
                                                        in={actividadesAbiertas.includes(actividad.idActividad)}
                                                        timeout="auto"
                                                        unmountOnExit
                                                    >
                                                        <List dense disablePadding sx={{ pl: 3 }}>
                                                            {actividad.materiales.map((material) => (
                                                                <ListItemButton
                                                                    key={material.idDetalleActividad}
                                                                    selected={seleccion?.titulo === material.titulo}
                                                                    onClick={() => verMaterial(material, actividad.titulo)}
                                                                    sx={{ borderRadius: 1 }}
                                                                >
                                                                    <ListItemText
                                                                        primary={material.titulo}
                                                                        secondary={
                                                                            material.archivoEvidencia
                                                                                ? formatearNombreEvidencia(
                                                                                      colaborador.nombre_persona,
                                                                                      material.titulo,
                                                                                      material.archivoEvidencia
                                                                                  )
                                                                                : 'Sin evidencia cargada'
                                                                        }
                                                                    />
                                                                    {material.archivoEvidencia && (
                                                                        <Tooltip title="Descargar evidencia">
                                                                            <span>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    disabled={descargando === material.idDetalleActividad}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        descargarEvidencia(material);
                                                                                    }}
                                                                                >
                                                                                    {descargando === material.idDetalleActividad ? (
                                                                                        <CircularProgress size={18} />
                                                                                    ) : (
                                                                                        <Download fontSize="small" />
                                                                                    )}
                                                                                </IconButton>
                                                                            </span>
                                                                        </Tooltip>
                                                                    )}
                                                                </ListItemButton>
                                                            ))}
                                                        </List>
                                                    </Collapse>
                                                </div>
                                            ))}
                                        </List>
                                    </Collapse>
                                </div>
                            ))}
                        </List>
                    )}
                </Paper>

                <Paper
                    variant="outlined"
                    sx={{ flex: '1 1 60%', p: 1.5, borderRadius: 2, minHeight: 460, display: 'flex', flexDirection: 'column' }}
                >
                    {!seleccion ? (
                        <Box sx={{ m: 'auto', textAlign: 'center' }}>
                            <Typography variant="body2" color="textSecondary">
                                Selecciona un material para ver la evidencia cargada por el colaborador.
                            </Typography>
                        </Box>
                    ) : seleccion.cargando ? (
                        <Box sx={{ m: 'auto' }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : seleccion.sinEvidencia ? (
                        <Box sx={{ m: 'auto', textAlign: 'center' }}>
                            <Typography variant="body2" color="textSecondary">
                                {seleccion.mensaje || 'El colaborador no ha cargado evidencia para este material.'}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    mb: 1.5,
                                    pb: 1.5,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider'
                                }}
                            >
                                <List dense disablePadding>
                                    <ListItem disableGutters sx={{ py: 0.25 }}>
                                        <ListItemText
                                            primary={
                                                <Typography variant="body2">
                                                    Actividad: <strong>{seleccion.tituloActividad || '-'}</strong>
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                    <ListItem disableGutters sx={{ py: 0.25 }}>
                                        <ListItemText
                                            primary={
                                                <Typography variant="body2">
                                                    Material:{' '}
                                                    <strong>
                                                        {seleccion.archivoEvidencia
                                                            ? formatearNombreEvidencia(
                                                                  colaborador.nombre_persona,
                                                                  seleccion.titulo,
                                                                  seleccion.archivoEvidencia
                                                              )
                                                            : seleccion.titulo}
                                                    </strong>
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                    {seleccion.material?.fechaCreacion && (
                                        <ListItem disableGutters sx={{ py: 0.25 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="caption" color="textSecondary">
                                                        Subido el: {seleccion.material.fechaCreacion}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    )}
                                </List>

                                {seleccion.material?.linkVideo && (
                                    <Button
                                        size="small"
                                        startIcon={<Fullscreen />}
                                        onClick={abrirPantallaCompleta}
                                        sx={{ flexShrink: 0, ...estiloBotonNotable }}
                                    >
                                        Ver en pantalla completa
                                    </Button>
                                )}
                            </Box>
                            <Box sx={{ flex: 1 }}>{renderMaterial(seleccion.material)}</Box>
                        </>
                    )}
                </Paper>
            </Box>

            <Dialog fullScreen open={pantallaCompleta} onClose={cerrarPantallaCompleta} disableEscapeKeyDown={false}>
                <Box onClick={cerrarPantallaCompleta} sx={{ position: 'fixed', inset: 0, backgroundColor: '#000', zIndex: 1 }} />

                <IconButton
                    onClick={cerrarPantallaCompleta}
                    sx={{
                        position: 'fixed',
                        top: 16,
                        right: 16,
                        zIndex: 3,
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        color: '#fff',
                        width: 48,
                        height: 48,
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
                    }}
                >
                    <Close />
                </IconButton>

                <Box
                    sx={{
                        position: 'fixed',
                        top: 24,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 3,
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        color: '#fff',
                        px: 2,
                        py: 0.75,
                        borderRadius: '999px',
                        pointerEvents: 'none'
                    }}
                >
                    <Typography variant="caption">Presiona ESC o toca la X para salir</Typography>
                </Box>

                <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        position: 'relative',
                        zIndex: 2,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 4
                    }}
                >
                    {seleccion?.material && renderMaterial(seleccion.material, window.innerHeight - 140)}
                </Box>
            </Dialog>
        </Box>
    );
};

export default ModalEvidenciaColaborador;
